import { useCallback, useEffect, useMemo, useState } from "react";

import { fetchLoomRebuildStatus, type LoomRebuildStatus } from "../../api/loomRebuild";
import type { LoomAuthoringDocument } from "./loomAuthoringModel";
import { loomAuthoringDocumentStateId } from "./loomAuthoringHistory";
import {
  initialLoomAuthoritativeRebuildRuntime,
  LoomAuthoritativeRebuildScheduler,
} from "./loomAuthoritativeRebuildScheduler";
import type { LoomNativeHybridAsset } from "./loomNativeHybridAsset";

export function useLoomAuthoritativeRebuild(
  projectId: string,
  document: LoomAuthoringDocument,
  asset: LoomNativeHybridAsset | null,
  automatic: boolean,
  previewActive: boolean,
) {
  const [runtime, setRuntime] = useState(initialLoomAuthoritativeRebuildRuntime);
  const [serviceStatus, setServiceStatus] = useState<LoomRebuildStatus | null>(null);
  const scheduler = useMemo(
    () => new LoomAuthoritativeRebuildScheduler(
      projectId, setRuntime, undefined, fetchLoomRebuildStatus,
    ),
    [projectId],
  );
  useEffect(() => {
    scheduler.activate();
    return () => scheduler.dispose();
  }, [scheduler]);
  useEffect(() => {
    const controller = new AbortController();
    fetchLoomRebuildStatus(projectId, controller.signal).then(setServiceStatus).catch(() => setServiceStatus(null));
    return () => controller.abort();
  }, [projectId]);
  const stateId = loomAuthoringDocumentStateId(document);
  scheduler.observe(document);
  useEffect(() => {
    if (previewActive) return undefined;
    if (!automatic) {
      scheduler.synchronize(document);
      return undefined;
    }
    if (asset && serviceStatus?.configured === true) scheduler.request(document, asset);
    return undefined;
  }, [asset, automatic, document.revision, previewActive, scheduler, serviceStatus?.configured, stateId]);
  const rebuild = useCallback(() => {
    if (asset) scheduler.request(document, asset);
  }, [asset, document, scheduler]);
  return { ...runtime, serviceStatus, rebuild, cancel: () => scheduler.cancel() };
}
